/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useRef, useEffect, useMemo } from 'react'
import cn from 'classnames'
import Icon from 'meinian-package/src/component/icon'
import ReactDOM from 'react-dom'
import css from './select.module.less'

export type SelectOption = {
  label: string
  value: unknown
}
export type OptionProps = SelectOption & {
  highlight?: string
  checked: boolean
  onClick?: (value: unknown, label: string) => void
}
export type FormItemProps = {
  onChange: (value: unknown) => void
  value: unknown
}
export type SelectProps = FormItemProps & {
  placeholder: string
  allowClear?: boolean
  multiple?: boolean
  /**
   * 远程搜索方法
   * @param value
   * @returns
   */
  onSearch?: (value: string) => Promise<void>
  /**
   * 本地搜索方法
   * @param option
   * @param index
   * @param keyword
   * @returns
   */
  onFilter?: (option: SelectOption, index: number, keyword: string) => boolean
  options: SelectOption[]
}
function Option({ label, value, checked, onClick, highlight }: OptionProps) {
  return (
    // eslint-disable-next-line jsx-a11y/tabindex-no-positive
    <a className={cn(css.option, { [css.checked]: checked })} onClick={() => onClick(value, label)} tabIndex={12}>
      <span
        dangerouslySetInnerHTML={{
          __html: highlight
            ? label.replace(new RegExp(highlight, 'gi'), `<span class=${css.highlight}>${highlight}</span>`)
            : label,
        }}
      />
      {checked && <Icon type="m-xiaoyan" className={css.icon} />}
    </a>
  )
}
const useVisible = ($select) => {
  const [overlayStyle, setOverlayStyle] = useState({})
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const { bottom, left, width } = $select.current.getBoundingClientRect()
    if (visible) {
      setOverlayStyle({
        top: bottom + 10,
        left,
        width,
        transform: 'scaleY(1)',
        opacity: 1,
      })
    } else {
      setOverlayStyle({
        top: bottom + 10,
        left,
        width,
        opacity: 0,
        transform: 'scaleY(0)',
      })
    }
  }, [visible])
  return { visible, setVisible, overlayStyle }
}
export default function Select(props: SelectProps) {
  const { placeholder, allowClear, value, options: opts, onFilter, multiple, onChange, onSearch } = props
  const [keyword, setKeyword] = useState('')
  const $select = useRef(null)
  const [loading, setLoading] = useState(false)
  const { visible, setVisible, overlayStyle } = useVisible($select)
  const $overlay = useRef(null)
  const $input = useRef(null)
  const [focus, setFocus] = useState(false)
  const options = useMemo(() => {
    // 本地搜索
    if (onFilter && !onSearch) {
      return opts.filter((item, index) => onFilter(item, index, keyword))
    }
    return opts
  }, [opts, keyword])
  useEffect(() => {
    if (visible && onSearch) {
      setLoading(true)
      onSearch('').then(() => setLoading(false))
    }
  }, [visible])
  const onChangeKeyword = async (val) => {
    setKeyword(val)
    // 当支持远程搜索时
    if (onSearch) {
      setLoading(true)
      await onSearch(val)
      setLoading(false)
    }
  }

  const onFocus = () => {
    setVisible(!visible)
    setFocus(true)
    if (multiple) {
      onChangeKeyword('')
    }
  }

  const onClear = () => {
    _onChange('')
    onChangeKeyword('')
  }

  const onKeyUp = (e) => {
    if (e.keyCode === 13) {
      setVisible(false)
    }
    // 上
    if (e.keyCode === 38) {
      $overlay.current.querySelector(`.${css.option}`).focus()
    }
    // 下
    if (e.keyCode === 40) {
      const focusElement = $overlay.current.contains(document.activeElement)
      if (focusElement) {
        focusElement.nextElementSibling.focus()
      } else {
        $overlay.current.querySelector(`.${css.option}`).focus()
      }
      e.preventDefault()
    }
  }

  const onBlur = (e) => {
    if (e.relatedTarget) {
      if ($select.current.contains(e.relatedTarget) || $overlay.current.contains(e.relatedTarget)) {
        return
      }
    }
    setFocus(false)
    onChangeKeyword('')
    setVisible(false)
  }

  const _onMultipleChange = (val: unknown) => {
    if (val === null) {
      onChange([])
      return
    }
    const hasValue = (value as []).some((m) => m === val)
    if (hasValue) {
      onChange((value as []).filter((m) => m !== val))
    } else {
      onChange([...(value as []), val])
    }
  }
  const _onChange = (val: unknown) => {
    onChange(val)
    setVisible(false)
    setFocus(false)
    onChangeKeyword('')
  }
  const onClearValue = () => {
    if (multiple) {
      onChange([])
    } else {
      onChange(null)
    }
  }

  const isNullValue = useMemo(() => {
    if (multiple) {
      return (value as []).length === 0
    }
    return value === null || value === undefined
  }, [value, multiple])

  const displayText = useMemo(() => {
    if (multiple) {
      if ((value as []).length === 0) return null
      return (value as []).map((m) => opts.find((n) => n.value === m).label).join(';')
    }
    return opts.find((m) => m.value === value)?.label || ''
  }, [value, opts, multiple])

  const placeholderText = useMemo(() => {
    if (isNullValue) return placeholder
    if (focus) {
      return displayText
    }
    return ''
  }, [placeholder, isNullValue, displayText, multiple, focus])
  const multipleValue = value as []
  return (
    <>
      <div
        // eslint-disable-next-line jsx-a11y/tabindex-no-positive
        tabIndex={1}
        onClick={() => $input.current.focus()}
        className={cn(css.select, { [css.active]: visible })}
        ref={$select}
      >
        <Icon type="m-sousuo" style={{ marginRight: 2 }} />
        <input
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onKeyUp}
          readOnly={!onFilter && !onSearch}
          type="text"
          ref={$input}
          placeholder={placeholderText}
          value={focus ? keyword : ''}
          onChange={(e) => onChangeKeyword(e.target.value)}
        />
        {!focus && (
          <span title={displayText} className={css.displayText}>
            {displayText}
          </span>
        )}
        {!isNullValue && multiple && multipleValue.length > 1 ? <span className={css.count}>{multipleValue.length}</span> : null}
        {keyword?.length > 0 && <Icon type="m-guanbi_tianchong" onClick={onClear} className={css.cleaner} />}
        <Icon type="m-Vxing_xia" className={css.icon} />
      </div>
      {ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/tabindex-no-positive
        <div ref={$overlay} tabIndex={2} onBlur={onBlur} className={css.overlay} style={overlayStyle}>
          {loading && <div className={css.loading} />}
          <nav className={css.options}>
            {allowClear && <Option onClick={onClearValue} checked={isNullValue} label="不限" value={null} />}
            {options?.map((option) => (
              <Option
                key={option.value as string}
                highlight={keyword}
                checked={multiple ? (value as [])?.includes(option.value as never) : value === option.value}
                label={option.label}
                onClick={multiple ? _onMultipleChange : _onChange}
                value={option.value}
              />
            ))}
          </nav>
        </div>,
        document.body
      )}
    </>
  )
}
